<template>
  <div class="form-content">
    <form @submit.prevent="handleSubmit">
      <div class="form-fields">
        <div
          v-for="field in currentStep.data.form"
          :key="field.name"
          class="form-field"
        >
          <label
            :for="field.name"
            class="field-label"
          >
            {{ field.label }}
            <span 
              v-if="field.required"
              class="tw-text-red-500 tw-ml-1"
            >*</span>
          </label>
          <div class="field-wrapper">
            <input
              :id="field.name"
              v-model="localFormData[field.name]"
              :type="field.type || 'text'"
              :name="field.name"
              :placeholder="field.placeholder"
              :required="field.required"
              class="field-input"
            >
            <div class="field-focus-indicator"></div>
          </div>
        </div>
      </div>
      <div class="form-actions">
        <button
          v-if="currentStep.data.previous_step"
          type="button"
          class="btn-back"
          @click="$emit('back', currentStep.data.previous_step)"
        >
          <svg 
            class="tw-w-5 tw-h-5 tw-mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path 
              stroke-linecap="round" 
              stroke-linejoin="round" 
              stroke-width="2" 
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <div class="tw-flex-1"></div>
        <button
          type="submit"
          class="btn-next"
        >
          Next
          <svg 
            class="tw-w-5 tw-h-5 tw-ml-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path 
              stroke-linecap="round" 
              stroke-linejoin="round" 
              stroke-width="2" 
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
export default {
  name: 'FormStep',
  props: {
    currentStep: {
      type: Object,
      required: true
    },
    formData: {
      type: Object,
      required: true
    }
  },
  emits: ['submit', 'back', 'update:formData'],
  data() {
    return {
      localFormData: {}
    };
  },
  watch: {
    formData: {
      immediate: true,
      handler(newVal) {
        this.localFormData = { ...newVal };
      }
    },
    localFormData: {
      deep: true,
      handler(newVal) {
        this.$emit('update:formData', newVal);
      }
    }
  },
  methods: {
    handleSubmit() {
      this.$emit('submit', this.localFormData);
    }
  }
};
</script>

<style scoped>
.form-content {
  @apply tw-px-8 tw-pb-8;
  animation: fadeIn 0.3s ease-out;
}

.form-fields {
  @apply tw-space-y-6;
}

.form-field {
  @apply tw-relative;
}

.field-label {
  @apply tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2;
}

.field-wrapper {
  @apply tw-relative;
}

.field-input {
  @apply tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg;
  @apply tw-text-gray-900 tw-placeholder-gray-400;
  @apply tw-transition-all tw-duration-200;
  @apply tw-shadow-sm;
  @apply tw-text-base;
}

.field-input:focus {
  @apply tw-outline-none tw-border-blue-500 tw-ring-2 tw-ring-blue-200;
  @apply tw-shadow-md;
}

.field-focus-indicator {
  @apply tw-absolute tw-bottom-0 tw-left-0 tw-w-0 tw-h-0.5;
  @apply tw-bg-gradient-to-r tw-from-blue-500 tw-to-purple-500;
  @apply tw-transition-all tw-duration-300;
}

.field-input:focus ~ .field-focus-indicator {
  @apply tw-w-full;
}

.form-actions {
  @apply tw-flex tw-items-center tw-mt-8 tw-pt-6;
  @apply tw-border-t tw-border-gray-200;
}

.btn-back,
.btn-next {
  @apply tw-inline-flex tw-items-center tw-justify-center;
  @apply tw-font-medium tw-transition-all tw-duration-200;
  @apply tw-rounded-lg tw-shadow-sm;
}

.btn-back {
  @apply tw-px-6 tw-py-3 tw-bg-gray-100 tw-text-gray-700;
}

.btn-back:hover {
  @apply tw-bg-gray-200 tw-shadow-md;
}

.btn-next {
  @apply tw-px-8 tw-py-3 tw-text-white tw-font-medium;
  background: linear-gradient(to right, #3b82f6, #9333ea);
  @apply tw-transform;
}

.btn-next:hover {
  background: linear-gradient(to right, #2563eb, #7c3aed);
  @apply tw-shadow-lg;
  transform: scale(1.05);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .form-content {
    @apply tw-px-6;
  }
  
  .form-actions {
    @apply tw-flex-col tw-space-y-3;
  }
  
  .btn-back,
  .btn-next {
    @apply tw-w-full;
  }
  
  .btn-back {
    @apply tw-order-2;
  }
  
  .btn-next {
    @apply tw-order-1;
  }
}

@media (max-width: 480px) {
  .field-input {
    @apply tw-text-base tw-px-3 tw-py-2.5;
  }
}

@media print {
  .btn-back,
  .btn-next {
    display: none !important;
  }
}
</style>
